import Image from 'next/image';
import Link from 'next/link';
import getContentType from '@/helpers/get-content-type';
import { CARD_FALLBACK_IMAGE } from '@/lib/utils/thumbnail-url';

interface CardData {
  image: {
    url: string;
    alt?: string;
  };
  title: string;
  label: string;
  minutes: number;
  seconds: number;
}

interface SliderData {
  title?: string;
  link?: {
    title?: string;
    href?: string;
  };
  cards?: any[]; // Course data from Contentstack
}

interface CardsSectionProps {
  slider?: SliderData;
  heading?: string;
  viewAllText?: string;
  viewAllLink?: string;
  cards?: CardData[];
  variant?: 'default' | 'grid'; // 'default' = 2 cols, 'grid' = 4 cols
  imageHeight?: number; // Custom image height
  fixedTitleHeight?: boolean; // Whether to use fixed title height
}

export function CardsSection({
  slider,
  cards: propCards = [],
  variant = 'default',
  imageHeight,
  fixedTitleHeight = false,
}: CardsSectionProps) {
  // Extract data from slider object if provided
  const headingValue = slider?.title || 'Featured Courses';
  const viewAllTextValue = slider?.link?.title || 'All Courses';
  const rawViewAllLink = slider?.link?.href || '#';
  // When "All courses" links to catalog, append param so catalog auto-selects Course filter
  const viewAllLinkValue = (() => {
    const pathOnly = rawViewAllLink.split('?')[0]?.replace(/\/$/, '') || '';
    const isCatalog = pathOnly === '/catalog' || pathOnly.endsWith('/catalog');
    if (!isCatalog) return rawViewAllLink;
    const separator = rawViewAllLink.includes('?') ? '&' : '?';
    return `${rawViewAllLink}${separator}contentType=course`;
  })();

  // Determine grid columns based on variant
  const gridCols = variant === 'grid' 
    ? 'grid-cols-1 tab:grid-cols-2 lg:grid-cols-4' 
    : 'grid-cols-1 tab:grid-cols-2 lg:grid-cols-2';

  // Determine responsive image height based on variant or prop
  // Mobile: smaller heights, Tablet: medium (2 cols), Desktop: full size
  const imgHeight = imageHeight 
    ? '' // Will use inline style
    : variant === 'grid' 
      ? 'h-[140px] tab:h-[150px] lg:h-[170px]' 
      : 'h-[175px] tab:h-[200px] lg:h-[355px]';
  
  // Use inline style for custom height if provided (not responsive)
  const imgStyle = imageHeight ? { height: `${imageHeight}px` } : undefined;

  // Determine title class based on fixedTitleHeight
  const titleClass = fixedTitleHeight 
    ? 'cs-h4 text-text-default-medium h-[48px] line-clamp-2 overflow-hidden'
    : 'cs-h4 text-text-default-medium';

  // Helper function to format duration
  const formatDuration = (duration: string | number | undefined): string => {
    if (!duration) return '';
    if (typeof duration === 'string') return duration;
    // If it's a number, assume it's in seconds and format it
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}m ${seconds}s`;
  };

  // Helper function to get label from taxonomies or content type
  const getLabel = (course: any) => {
    // Check content type first (most reliable for learning paths vs courses)
    if (course?._content_type_uid === 'learning_pathways') {
      return 'Learning Path';
    }
    // Fallback to taxonomies
    if (course?.taxonomies && Array.isArray(course.taxonomies)) {
      return getContentType(course.taxonomies, 'content_type') || 'Course';
    }
    return 'Course'; // Default
  };

  return (
    <section className="section w-full py-8 lg:py-12">
      <div className="mx-auto w-full">
        {/* Header with heading and optional View all */}
        <div className="flex items-center justify-between mb-6 desktop:mb-8">
          <h2 className="!text-text-default-medium cs-h6 tab:cs-h4 desktop:cs-h3 " id="featured-courses-heading">{headingValue}</h2>
          {slider?.link && (
            <Link
              href={viewAllLinkValue}
              className="view-all-link cs-caption tab:cs-s-body desktop:cs-m-body !text-amethyst-accent"
              aria-label={`${viewAllTextValue} for ${headingValue}`}
            >
              {viewAllTextValue}
            </Link>
          )}
        </div>

        {/* Cards Grid */}
        <div className={`grid ${gridCols} gap-6 tab:gap-8 lg:gap-8 items-stretch`}>
          {slider?.cards?.map((cardItem: any, index: number) => {
            // Extract the actual course object from card array
            const course = cardItem?.card?.[0];
            if (!course) return null;

            // Get duration and type from enriched course data (from CardsSectionClient)
            const durationStr = course.duration || '';
            const label = course.courseType || getLabel(course);

            // Construct href from Contentstack content type
            const courseUrl = !course.url
              ? '#'
              : course._content_type_uid === 'learning_pathways'
                ? course.url.startsWith('/learning-paths')
                  ? course.url
                  : `/learning-paths${course.url}`
                : course.url.startsWith('/courses')
                  ? course.url
                  : `/courses${course.url}`;

            return (
              <Link
                key={index}
                href={courseUrl}
                className="flex flex-col w-full h-full card-hover-zoom group"
              >
                {/* Image */}
                <div 
                  className={`relative w-full ${!imageHeight ? imgHeight : ''} overflow-hidden rounded-lg`}
                  style={imgStyle}
                >
                  <Image
                    src={
                      course.thumbnail?.url && course.thumbnail.url.trim() !== ''
                        ? course.thumbnail.url
                        : course.learning_pathway_details?.thumbnail?.url && course.learning_pathway_details.thumbnail.url.trim() !== ''
                        ? course.learning_pathway_details.thumbnail.url
                        : course.open_graph?.image?.url && course.open_graph.image.url.trim() !== ''
                        ? course.open_graph.image.url
                        : CARD_FALLBACK_IMAGE
                    }
                    alt={
                      course.thumbnail?.alt ||
                      course.thumbnail?.title ||
                      course.learning_pathway_details?.thumbnail?.alt ||
                      course.learning_pathway_details?.thumbnail?.title ||
                      course.title ||
                      ''
                    }
                    fill
                    className="object-cover card-zoom-image"
                    sizes={variant === 'grid'
                      ? "(max-width: 810px) 100vw, (max-width: 1200px) 50vw, 25vw"
                      : "(max-width: 810px) 100vw, (min-width: 810px) 50vw"
                    }
                    priority={index === 0}
                    fetchPriority={index === 0 ? 'high' : undefined}
                  />
                </div>

                {/* Content below image */}
                <div className={`p-6 flex flex-col gap-1 bg-surface-2 card-content-hover transition-colors duration-300 ${fixedTitleHeight ? 'min-h-[120px]' : ''}`}>
                  {/* Title */}
                  <h3 className={titleClass}>{course.title || ''}</h3>

                  {/* Label and Duration with pipe separator */}
                  <div className="flex items-center gap-2 text-text-default-light">
                    <span className="cs-caption">{label}</span>
                    {durationStr && (
                      <>
                        <span className="cs-caption">|</span>
                        <span className="cs-caption">{durationStr}</span>
                      </>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
